import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import {
  BULK_OCCASION_TYPES,
  defaultDeliveryAnchorForCadence,
  isBulkProgramActive,
  type BulkCelebrationCadence,
  type DeliveryAnchor,
} from "@/lib/bulkCelebrations";
import { useBulkProgram, useSaveBulkProgram } from "@/hooks/useBulkCelebrations";

type Props = {
  companyId: string;
  addressOptions: { id: string; label: string }[];
};

const CADENCE_OPTIONS: { value: BulkCelebrationCadence; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Every two weeks" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
];

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export function BulkCelebrationSettingsPanel({ companyId, addressOptions }: Props) {
  const { toast } = useToast();
  const programQuery = useBulkProgram(companyId);
  const save = useSaveBulkProgram(companyId);
  const program = programQuery.data;

  const [enabled, setEnabled] = useState(false);
  const [cadence, setCadence] = useState<BulkCelebrationCadence>("monthly");
  const [tier, setTier] = useState<"standard" | "premium" | "luxury">("standard");
  const [addressId, setAddressId] = useState<string>("");
  const [occasions, setOccasions] = useState<string[]>([...BULK_OCCASION_TYPES]);
  const [anchorKind, setAnchorKind] = useState<DeliveryAnchor["kind"]>(
    "nth_weekday_of_period",
  );
  const [weekday, setWeekday] = useState(5);
  const [nth, setNth] = useState<1 | -1>(-1);
  const [offsetDays, setOffsetDays] = useState(0);
  const [fixedDay, setFixedDay] = useState(15);

  useEffect(() => {
    if (!program) return;
    setEnabled(isBulkProgramActive(program));
    setCadence(program.cadence);
    setTier(program.default_tier);
    setAddressId(program.delivery_address_id ?? "");
    setOccasions(
      program.occasion_types?.length ? program.occasion_types : [...BULK_OCCASION_TYPES],
    );
    const raw = program.delivery_anchor as DeliveryAnchor | null;
    const anchor =
      raw ??
      defaultDeliveryAnchorForCadence(
        program.cadence,
        program.enabled_at ? new Date(program.enabled_at) : null,
      );
    setAnchorKind(anchor.kind);
    if (anchor.kind === "nth_weekday_of_period") {
      setWeekday(anchor.weekday);
      setNth(anchor.nth);
    } else if (anchor.kind === "offset_from_period_start") {
      setOffsetDays(anchor.days);
    } else if (anchor.kind === "fixed_day_of_period") {
      setFixedDay(anchor.day_of_month);
    }
  }, [program]);

  const buildAnchor = (): DeliveryAnchor => {
    if (anchorKind === "first_celebration_in_period") {
      return { kind: "first_celebration_in_period" };
    }
    if (anchorKind === "offset_from_period_start") {
      return { kind: "offset_from_period_start", days: offsetDays };
    }
    if (anchorKind === "fixed_day_of_period") {
      return { kind: "fixed_day_of_period", day_of_month: fixedDay };
    }
    return { kind: "nth_weekday_of_period", weekday, nth };
  };

  const toggleOccasion = (t: string, on: boolean) => {
    setOccasions((prev) => {
      const set = new Set(prev);
      if (on) set.add(t);
      else set.delete(t);
      return [...set];
    });
  };

  const onSave = async () => {
    if (enabled && occasions.length === 0) {
      toast({
        title: "Pick at least one occasion",
        variant: "destructive",
      });
      return;
    }
    try {
      await save.mutateAsync({
        enabled,
        cadence,
        default_tier: tier,
        delivery_address_id: addressId || null,
        delivery_anchor: buildAnchor(),
        occasion_types: occasions,
      });
      toast({ title: "Bulk celebrations saved" });
    } catch (e) {
      toast({
        title: "Couldn't save",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    }
  };

  return (
    <div id="bulk" className="scroll-mt-24 space-y-4 rounded-xl border border-charcoal/10 bg-white p-6">
      <div>
        <h3 className="text-lg font-semibold text-navy">Bulk celebrations</h3>
        <p className="mt-1 text-sm text-charcoal/60">
          One shared dessert per period for everyone in the roster — one invoice.
          Per-person non-dessert gifts stay on the normal celebration flow.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium text-navy">
        <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(v === true)} />
        Enable bulk celebrations
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label>Cadence</Label>
          <Select value={cadence} onValueChange={(v) => setCadence(v as BulkCelebrationCadence)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CADENCE_OPTIONS.map((c) => (
                <SelectItem key={c.value} value={c.value}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Default tier</Label>
          <Select value={tier} onValueChange={(v) => setTier(v as typeof tier)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Thoughtful</SelectItem>
              <SelectItem value="premium">Impressive</SelectItem>
              <SelectItem value="luxury">Unforgettable</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Delivery address</Label>
        <Select value={addressId || "_none"} onValueChange={(v) => setAddressId(v === "_none" ? "" : v)}>
          <SelectTrigger className="mt-1 max-w-md">
            <SelectValue placeholder="Company default" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">Use company default</SelectItem>
            {addressOptions.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Occasions in bulk periods</Label>
        <div className="mt-2 flex flex-wrap gap-3">
          {BULK_OCCASION_TYPES.map((t) => (
            <label key={t} className="flex items-center gap-2 text-sm capitalize">
              <Checkbox
                checked={occasions.includes(t)}
                onCheckedChange={(v) => toggleOccasion(t, v === true)}
              />
              {t.replace(/_/g, " ")}
            </label>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Delivery timing within each period</Label>
        <Select
          value={anchorKind}
          onValueChange={(v) => setAnchorKind(v as DeliveryAnchor["kind"])}
        >
          <SelectTrigger className="max-w-md">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="nth_weekday_of_period">Specific weekday (e.g. last Friday)</SelectItem>
            <SelectItem value="first_celebration_in_period">First celebration in period</SelectItem>
            <SelectItem value="offset_from_period_start">Days after period starts</SelectItem>
            <SelectItem value="fixed_day_of_period">Fixed day of month</SelectItem>
          </SelectContent>
        </Select>
        {anchorKind === "nth_weekday_of_period" && (
          <div className="flex flex-wrap gap-2">
            <Select value={String(weekday)} onValueChange={(v) => setWeekday(Number(v))}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map((d, i) => (
                  <SelectItem key={d} value={String(i)}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(nth)} onValueChange={(v) => setNth(Number(v) as 1 | -1)}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">First in period</SelectItem>
                <SelectItem value="-1">Last in period</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {anchorKind === "offset_from_period_start" && (
          <input
            type="number"
            min={0}
            max={90}
            className="mt-1 w-24 rounded border px-2 py-1 text-sm"
            value={offsetDays}
            onChange={(e) => setOffsetDays(Number(e.target.value))}
          />
        )}
        {anchorKind === "fixed_day_of_period" && (
          <input
            type="number"
            min={1}
            max={31}
            className="mt-1 w-24 rounded border px-2 py-1 text-sm"
            value={fixedDay}
            onChange={(e) => setFixedDay(Number(e.target.value))}
          />
        )}
      </div>

      <Button variant="primary" size="sm" onClick={() => void onSave()} disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save bulk program"}
      </Button>
    </div>
  );
}
